import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, FormControlLabel, Switch, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createExpenseCategory, updateExpenseCategory, type ExpenseCategory } from '../../api/deconturi'
import { errorMessage } from '../../api/client'

const schema = z.object({
  code: z.string().min(1, 'Câmp obligatoriu').max(32),
  name: z.string().min(1, 'Câmp obligatoriu').max(128),
  deductible: z.boolean(),
  isActive: z.boolean(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { code: '', name: '', deductible: true, isActive: true }

export default function ExpenseCategoryFormDialog({ category, onClose }: { category: ExpenseCategory | null | 'new'; onClose: () => void }) {
  const open = category !== null
  const editing = category !== null && category !== 'new'
  const qc = useQueryClient()

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema), defaultValues: EMPTY,
  })

  useEffect(() => {
    if (editing) {
      const c = category as ExpenseCategory
      reset({ code: c.code, name: c.name, deductible: c.deductible, isActive: c.isActive })
    } else if (category === 'new') {
      reset(EMPTY)
    }
  }, [category, editing, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (editing) return updateExpenseCategory((category as ExpenseCategory).id, values)
      return createExpenseCategory(values)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['financiar', 'categorii-cheltuieli'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare categorie de cheltuială' : 'Categorie de cheltuială nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="code" control={control} render={({ field }) => (
                <TextField {...field} label="Cod" disabled={editing} fullWidth error={!!errors.code} helperText={errors.code?.message} />
              )} />
              <Controller name="name" control={control} render={({ field }) => (
                <TextField {...field} label="Denumire" fullWidth error={!!errors.name} helperText={errors.name?.message} />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="deductible" control={control} render={({ field }) => (
                <FormControlLabel control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Deductibilă fiscal" />
              )} />
              <Controller name="isActive" control={control} render={({ field }) => (
                <FormControlLabel control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Activă" />
              )} />
            </Stack>
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
